import {
  ClickAwayListener,
  Collapse,
  FormControlLabel,
  Grid,
  Switch,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import {
  Close,
  Delete,
  Edit,
  Email,
  History,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Link,
  PersonAddAlt,
  Timeline,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import '../OrderBoxPopper.scss';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { useAuth } from '../../../session/InternalAuthProvider';
import { getOrderPage } from '../../../common/AccountUtils';
import { useCommonDialogs } from '../../../common/dialogs/CommonDialogsContext';
import ResponsiveDialog from '../../../common/ui/ResponsiveDialog';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import EditOrderDialog from '../../dialogs/EditOrderDialog/EditOrderDialog';
import SendEmailDialog from '../../dialogs/SendEmailDialog/SendEmailDialog';
import { EnrichedEventData, OrderData } from '../../../api';
import { ParticipantsTableTabValue, TablesTabs } from './components/TablesTabs';
import CheckinDialog from '../../dialogs/CheckinDialog/CheckinDialog';
import CopyToClipboardButton from '../../dialogs/CopyToClipboardButton';
import { useEventOrders } from '../../swr/useEventOrders';
import { calculateTotalCheckedInPersons, EventBoxDialogInfoHeader } from './components/EventBoxDialogInfoHeader';
import { useLanguage } from '../../../common/GeneralUtils';
import { EventActivityLog } from './components/EventActivityLog';

type EventBoxDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  event: EnrichedEventData;
  openEvent: (event: EnrichedEventData) => void;
  getNextEvent: () => EnrichedEventData | undefined;
  getPreviousEvent: () => EnrichedEventData | undefined;
};
const EventBoxDialog = ({ open, setOpen, event, openEvent, getNextEvent, getPreviousEvent }: EventBoxDialogProps) => {
  const { t } = useTranslation();
  const { openDeleteEventDialog, deleteOrderDialogState, editOrderDrawerState, openExperienceAvailabilityDrawer } =
    useCalendar();
  const posthog = usePostHog();
  const [participantsTableActiveTabValue, setParticipantsTableActiveTabValue] = useState(
    ParticipantsTableTabValue.ORDERS,
  );
  const { isLoading, items: ordersList } = useEventOrders({ eventId: event.id });

  const { addOrder, updateOrder } = useEventOrders({ eventId: event.id });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // @ts-ignore
  const { openActivityDialog } = useCommonDialogs();
  const { authState } = useAuth();
  const [order, setOrder] = useState<OrderData | undefined>(undefined);
  const orderPage = getOrderPage(authState.account, event.order_page_id);
  const { readOnly, isAtLeastSupervisor } = UseAuthPermissions();
  const [editOrderIsOpen, setEditOrderIsOpen] = useState(false);
  const [sendMessageDialogIsOpen, setSendMessageDialogIsOpen] = useState(false);
  const [checkInDialogIsOpen, setCheckInDialogIsOpen] = useState(false);
  const isEventToday = moment(event.date).isSame(moment(), 'day');
  const eventTime = moment(event.date).startOf('day').add(event.time, 'seconds');
  const isAtLeastOneHourBeforeEvent = moment().isAfter(eventTime.subtract(1, 'hour'));
  const [waitingListUpdateDialogIsOpen, setWaitingListUpdateDialogIsOpen] = useState(false);
  const [eventActivityLogOpen, setEventActivityLogOpen] = useState(false);
  const shouldStartInEventMode = isEventToday && isAtLeastOneHourBeforeEvent && isSmallScreen;
  // when in event we want to show only the table with no tabs or info
  const [isInEventMode, setIsInEventMode] = React.useState(shouldStartInEventMode);
  const language = useLanguage();
  const nextEvent = getNextEvent();
  const previousEvent = getPreviousEvent();
  const handleExpandClick = () => {
    setIsInEventMode(!isInEventMode);
  };
  return (
    <ResponsiveDialog
      fullScreen
      isOpen={open}
      setOpen={setOpen}
      content={
        <ClickAwayListener
          onClickAway={() => {
            if (
              !editOrderIsOpen &&
              !editOrderDrawerState.isOpen &&
              !deleteOrderDialogState.isOpen &&
              !checkInDialogIsOpen &&
              !waitingListUpdateDialogIsOpen &&
              !eventActivityLogOpen
            ) {
              setOpen(false);
              setEditOrderIsOpen(false);
            }
          }}>
          <Grid>
            <Grid container flexDirection="column" px={2} pt={2} overflow="hidden" flexWrap="nowrap">
              <Grid container flexDirection="column" justifyContent="space-between">
                <Grid item display="flex" justifyContent="flex-end" alignItems="start">
                  <Grid
                    container
                    flexDirection={language === 'he' ? 'row-reverse' : 'row-reverse'}
                    justifyContent={'flex-end'}>
                    <Tooltip title={t('next')} arrow>
                      <span>
                        <IconButton disabled={!nextEvent} onClick={() => openEvent(nextEvent!)}>
                          {language === 'he' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('previous')} arrow>
                      <span>
                        <IconButton disabled={!previousEvent} onClick={() => openEvent(previousEvent!)}>
                          {language === 'he' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('add')} arrow>
                    <span>
                      <IconButton disabled={!isAtLeastSupervisor} onClick={() => setEditOrderIsOpen(true)}>
                        <PersonAddAlt />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={!isAtLeastSupervisor ? t('noPermissions') : t('calendar.event_popper.link_to_sell')}
                    arrow>
                    <span>
                      <IconButton
                        disabled={!isAtLeastSupervisor}
                        onClick={() =>
                          posthog.capture('Copy link to event', {
                            accountId: event.account_id,
                            eventId: event.id,
                          })
                        }>
                        <CopyToClipboardButton
                          IconComponent={Link}
                          text={`${window.location.origin}/order/${event.account_id}/${event.experience_id}/${event.order_page_id}?event=${event.id}`}
                          orderPageId={event.order_page_id}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !isAtLeastSupervisor
                        ? t('noPermissions')
                        : participantsTableActiveTabValue === ParticipantsTableTabValue.WAITING_LIST
                        ? t('calendar.event_popper.noWaitingListEmail')
                        : t('sendMessage')
                    }
                    arrow>
                    <span>
                      <IconButton
                        disabled={
                          !isAtLeastSupervisor ||
                          participantsTableActiveTabValue === ParticipantsTableTabValue.WAITING_LIST
                        }
                        onClick={() => setSendMessageDialogIsOpen(true)}>
                        <Email />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={readOnly ? t('noPermissions') : t('delete')} arrow>
                    <span>
                      <IconButton
                        disabled={readOnly}
                        onClick={() => {
                          openDeleteEventDialog({ event });
                          setOpen(false);
                        }}>
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('edit')} arrow>
                    <span>
                      <IconButton
                        disabled={!isAtLeastSupervisor}
                        onClick={() => {
                          openExperienceAvailabilityDrawer({ event, orderPageId: event.order_page_id });
                          setOpen(false);
                        }}>
                        <Edit />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('activity.activity_timeline')} arrow>
                    <span>
                      <IconButton
                        disabled={!isAtLeastSupervisor}
                        onClick={() => {
                          openActivityDialog('event', { eventId: event.id });
                          setOpen(false);
                        }}>
                        <Timeline />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('auditlog.event_activity_log')} arrow>
                    <span>
                      <IconButton disabled={!isAtLeastSupervisor} onClick={() => setEventActivityLogOpen(true)}>
                        <History />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <IconButton
                    onClick={() => {
                      setOpen(false);
                    }}>
                    <Tooltip title={t('close')} arrow>
                      <Close />
                    </Tooltip>
                  </IconButton>
                </Grid>
                <Grid container alignItems="center" justifyContent="space-between">
                  {isSmallScreen && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isInEventMode}
                          onChange={handleExpandClick}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      }
                      label={<Typography>{t('orders.eventView')}</Typography>}
                    />
                  )}
                  {isInEventMode && (
                    <Grid flexDirection="column" item>
                      <Typography variant="body1" color="#555555">
                        {t('calendar.event_popper.total_checkeding', {
                          total: isLoading ? 0 : calculateTotalCheckedInPersons(ordersList),
                        })}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                <Collapse in={!isInEventMode} timeout="auto" unmountOnExit>
                  <EventBoxDialogInfoHeader event={event} isLoading={isLoading} ordersList={ordersList} />
                </Collapse>
              </Grid>
            </Grid>
            <Grid p={2} pt={isSmallScreen ? 0 : 2}>
              <TablesTabs
                setCheckInDialogIsOpen={setCheckInDialogIsOpen}
                setOrder={setOrder}
                event={event}
                orderPage={orderPage}
                setEditOrderIsOpen={setEditOrderIsOpen}
                isInEventMode={isInEventMode}
                activeTab={participantsTableActiveTabValue}
                setActiveTab={setParticipantsTableActiveTabValue}
                waitingListUpdateDialogIsOpen={waitingListUpdateDialogIsOpen}
                setWaitingListUpdateDialogIsOpen={setWaitingListUpdateDialogIsOpen}
              />
            </Grid>
            <SendEmailDialog
              isOpen={sendMessageDialogIsOpen}
              setIsOpen={setSendMessageDialogIsOpen}
              eventId={event.id}
            />
            <CheckinDialog
              order={order}
              isOpen={checkInDialogIsOpen}
              setIsOpen={setCheckInDialogIsOpen}
              updateItem={updateOrder}
            />
            <EditOrderDialog
              editOrderDialogState={{
                order,
                isOpen: editOrderIsOpen,
                orderPageId: orderPage.id,
                parentId: event.id,
                parent: event,
              }}
              closeEditOrderDialog={() => {
                setEditOrderIsOpen(false);
                setOrder(undefined);
              }}
              addOrder={async (orderToUpsert) => addOrder(orderToUpsert)}
              updateOrder={(orderToUpdate) => updateOrder(orderToUpdate)}
            />
            <EventActivityLog
              eventId={event.id}
              open={eventActivityLogOpen}
              onClose={() => setEventActivityLogOpen(false)}
            />
          </Grid>
        </ClickAwayListener>
      }
    />
  );
};

export default EventBoxDialog;
